// Step1SalonTarih.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import {
	Box,
	Grid,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormHelperText,
	Typography,
	Stack,
	Chip,
	CircularProgress
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import type { FormValues } from "./MainStepper";
import EvlendirmeBasvuruAPI, { ReadAllNikahSaatBySalonTarihRequest } from "../../../../../src/apis/OB/SaveApi/EvlendirmeBasvuruAPI";

type Option = { id: string; name: string };

interface TimeSlot {
	saat: string;
	musait: boolean;
}

const Step1SalonTarih: React.FC = () => {
	const { values, setFieldValue, errors, touched, setFieldTouched } = useFormikContext<FormValues>();
	const [evlenmeTurleri, setEvlenmeTurleri] = useState<Option[]>([]);
	const [evlenmeYeriTurleri, setEvlenmeYeriTurleri] = useState<Option[]>([]);
	const [nikahSalonlari, setNikahSalonlari] = useState<Option[]>([]);
	const [availableTimes, setAvailableTimes] = useState<TimeSlot[]>([]);
	const [loadingTimes, setLoadingTimes] = useState<boolean>(false);

	const selectedDate: Dayjs | null = useMemo(() => {
		const val = values?.step1?.tarih;
		return val ? dayjs(val) : null;
	}, [values?.step1?.tarih]);

	// API'den dropdown verilerini çek
	useEffect(() => {
		let mounted = true;

		const fetchData = async () => {
			try {
				const turlarRes = await EvlendirmeBasvuruAPI.readAllEvlenmeTur();
				const yerTurleriRes = await EvlendirmeBasvuruAPI.readAllEvlenmeYeriTur();
				const salonlarRes = await EvlendirmeBasvuruAPI.readAllNikahSalonu();

				if (!mounted) return;
				if (turlarRes?.data && Array.isArray(turlarRes.data)) {
					const turlar = turlarRes.data.map((item: any) => ({
						id: Number(item.kodu), // ← Number'a çevir
						name: item.adi
					}));
					setEvlenmeTurleri(turlar);
				}

				if (yerTurleriRes?.data && Array.isArray(yerTurleriRes.data)) {
					const yerTurleri = yerTurleriRes.data.map((item: any) => ({
						id: Number(item.kodu), // ← Number'a çevir
						name: item.adi
					}));
					setEvlenmeYeriTurleri(yerTurleri);
				}

				if (salonlarRes?.data && Array.isArray(salonlarRes.data)) {
					const salonlar = salonlarRes.data.map((item: any) => ({
						id: Number(item.kodu), // ← Number'a çevir
						name: item.salonAdi
					}));
					setNikahSalonlari(salonlar);
				}
			} catch (error) {
				console.error("Veri yükleme hatası:", error);
			}
		};

		fetchData();

		return () => {
			mounted = false;
		};
	}, []);

	// Seçilen tarih için uygun saatleri çek
	useEffect(() => {
		let mounted = true;

		const fetchAvailableTimes = async () => {
			if (!selectedDate || !values?.step1?.nikahSalonuId) {
				setAvailableTimes([]);
				return;
			}

			setLoadingTimes(true);

			try {
				const year = selectedDate.year();
				const month = selectedDate.month(); // 0-11
				const day = selectedDate.date();
				const tarihDate = new Date(year, month, day, 12, 0, 0); // Öğlen 12:00 ver

				const request = new ReadAllNikahSaatBySalonTarihRequest(
					Number(values.step1.nikahSalonuId),
					tarihDate
				);

				const result = await EvlendirmeBasvuruAPI.readAllNikahSaatBySalonTarih(request);

				if (!mounted) return;

				const rawArray: any[] = result.data || [];

				const times: TimeSlot[] = rawArray
					.map((item: any) => {
						let saatStr: string | null = null;

						// Öncelik sırasına göre saat alanlarını kontrol et
							if (item['nikahBaslama']) {
								const rawValue = item['nikahBaslama'];
								
								// String ise direkt kullan (HH:MM formatında olmalı)
								if (typeof rawValue === 'string') {
									const match = rawValue.match(/\d{2}:\d{2}/);
									if (match) {
										saatStr = match[0];
									}
								}
								// Date objesi ise format'la
								else if (rawValue instanceof Date) {
									saatStr = dayjs(rawValue).format("HH:mm");
								}
							
						}

						if (!saatStr) {
							// saat parse edilemediyse eliyoruz 
							console.warn("Saat parse edilemedi, item:", item);
							return null;
						}

						// Prosedür sadece müsait saatleri döndürdüğü için musait = true
						return { saat: saatStr, musait: true };
					})
					.filter(Boolean) as TimeSlot[];

				// Duplicate saatleri temizle
				const uniqueTimesMap = new Map<string, TimeSlot>();
				times.forEach(t => {
					if (!uniqueTimesMap.has(t.saat)) {
						uniqueTimesMap.set(t.saat, t);
					}
				});

				const uniqueTimes = Array.from(uniqueTimesMap.values());

				// Saatlere göre sırala
				uniqueTimes.sort((a, b) => {
					const timeA = dayjs(`2000-01-01 ${a.saat}`);
					const timeB = dayjs(`2000-01-01 ${b.saat}`);
					return timeA.isBefore(timeB) ? -1 : timeA.isAfter(timeB) ? 1 : 0;
				});

				setAvailableTimes(uniqueTimes);

				// Eğer seçili saat listede yoksa temizle
				if (values?.step1?.saat) {
					const selectedTimeStr = dayjs(values.step1.saat).format('HH:mm');
					const exists = uniqueTimes.some(t => t.saat === selectedTimeStr);
					if (!exists) {
						setFieldValue("step1.saat", "");
					}
				}
			} catch (error) {
				console.error("Saatler yükleme hatası:", error);
				if (mounted) setAvailableTimes([]);
			} finally {
				if (mounted) setLoadingTimes(false);
			}
		};

		fetchAvailableTimes();

		return () => {
			mounted = false;
		};
	}, [selectedDate, values?.step1?.nikahSalonuId]);
	

	const handleDateChange = (newDate: Dayjs | null) => {
		if (newDate) {
			const dateStr = newDate.format("YYYY-MM-DD");
			setFieldValue("step1.tarih", dateStr);
			setFieldValue("step1.saat", "");
		} else {
			setFieldValue("step1.tarih", "");
			setFieldValue("step1.saat", "");
		}
		setFieldTouched("step1.tarih", true, false);
	};

	const shouldDisableDate = (date: Dayjs) => {
		// Geçmiş tarihleri devre dışı bırak
		return date.isBefore(dayjs(), 'day');
	};

	const handleTimeSelect = (timeStr: string) => {
		if (!values?.step1?.tarih) return;
		const fullDateTime = `${values.step1.tarih}T${timeStr}:00`;
		setFieldValue("step1.saat", fullDateTime);
		
		setFieldTouched("step1.saat", true, false);
	};

	return (
		<Box>
			<Grid container spacing={2}>
				<Grid item xs={12} md={4}>
					<FormControl 
						fullWidth 
						error={Boolean(touched?.step1?.evlenmeTuruId && errors?.step1?.evlenmeTuruId)} 
						size="small"
					>
						<InputLabel id="evlenme-turu-label">Evlenme Türü</InputLabel>
						<Select
							labelId="evlenme-turu-label"
							label="Evlenme Türü"
							value={values?.step1?.evlenmeTuruId || ""}
							onChange={(e) => setFieldValue("step1.evlenmeTuruId", Number(e.target.value))}
						>
							{evlenmeTurleri.map((o) => (
								<MenuItem key={o.id} value={o.id}>
									{o.name}
								</MenuItem>
							))}
						</Select>
						{touched?.step1?.evlenmeTuruId && errors?.step1?.evlenmeTuruId && (
							<FormHelperText>{String(errors.step1.evlenmeTuruId)}</FormHelperText>
						)}
					</FormControl>
				</Grid>

				<Grid item xs={12} md={4}>
					<FormControl fullWidth size="small">
						<InputLabel id="evlenme-yeri-turu-label">Evlenme Yeri Türü</InputLabel>
						<Select
							labelId="evlenme-yeri-turu-label"
							label="Evlenme Yeri Türü"
							value={values?.step1?.evlenmeYeriTuruId || ""}
							onChange={(e) => setFieldValue("step1.evlenmeYeriTuruId", Number(e.target.value))}
						>
							{evlenmeYeriTurleri.map((o) => (
								<MenuItem key={o.id} value={o.id}>
									{o.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>

				<Grid item xs={12} md={4}>
					<FormControl 
						fullWidth 
						error={Boolean(touched?.step1?.nikahSalonuId && errors?.step1?.nikahSalonuId)} 
						size="small"
					>
						<InputLabel id="nikah-salonu-label">Nikah Salonu</InputLabel>
						<Select
							labelId="nikah-salonu-label"
							label="Nikah Salonu"
							value={values?.step1?.nikahSalonuId || ""}
							onChange={(e) => {
								setFieldValue("step1.nikahSalonuId", Number(e.target.value));
								// Salon değişince seçili saati temizle
								setFieldValue("step1.saat", "");
							}}
						>
							{nikahSalonlari.map((o) => (
								<MenuItem key={o.id} value={o.id}>
									{o.name}
								</MenuItem>
							))}
						</Select>
						{touched?.step1?.nikahSalonuId && errors?.step1?.nikahSalonuId && (
							<FormHelperText>{String(errors.step1.nikahSalonuId)}</FormHelperText>
						)}
					</FormControl>
				</Grid>
			</Grid>

			<Box mt={3}>
				<Typography variant="subtitle1" gutterBottom>
					Tarih ve Saat Seçimi
				</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} md={6}>
						<Box position="relative" borderRadius={1}>
							<LocalizationProvider dateAdapter={AdapterDayjs}>
								<DateCalendar
									value={selectedDate}
									onChange={handleDateChange}
									shouldDisableDate={shouldDisableDate}
								/>
							</LocalizationProvider>
						</Box>
						{touched?.step1?.tarih && errors?.step1?.tarih && (
							<FormHelperText error>{String(errors.step1.tarih)}</FormHelperText>
						)}
					</Grid>

					<Grid item xs={12} md={6}>
						<Typography variant="body2" gutterBottom>
							Uygun Saatler
						</Typography>
						{!values?.step1?.nikahSalonuId ? (
							<Typography variant="body2" color="text.secondary">
								Lütfen önce bir nikah salonu seçiniz.
							</Typography>
						) : !selectedDate ? (
							<Typography variant="body2" color="text.secondary">
								Lütfen bir tarih seçiniz.
							</Typography>
						) : loadingTimes ? (
							<Stack direction="row" alignItems="center" spacing={1}>
								<CircularProgress size={18} />
								<Typography variant="body2">Saatler yükleniyor…</Typography>
							</Stack>
						) : availableTimes.length > 0 ? (
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))",
									gap: 1,
									alignItems: "center"
								}}
							>
								{availableTimes.map((timeObj) => {
									const selectedTimeStr = values?.step1?.saat 
										? dayjs(values.step1.saat).format('HH:mm')
										: '';
									const isSelected = selectedTimeStr === timeObj.saat;

									return (
										<Chip
											key={timeObj.saat}
											label={timeObj.saat}
											color={isSelected ? "primary" : "success"}
											variant={isSelected ? "filled" : "outlined"}
											onClick={() => handleTimeSelect(timeObj.saat)}
											sx={{
												cursor: "pointer",
												justifySelf: "center",
												minWidth: 72,
												paddingLeft: 1.2,
												paddingRight: 1.2
											}}
										/>
									);
								})}
							</Box>
						) : (
							<Typography variant="body2" color="text.secondary">
								Bu tarih için uygun saat bulunamadı. Lütfen başka bir tarih seçiniz.
							</Typography>
						)}
						{touched?.step1?.saat && errors?.step1?.saat && (
							<FormHelperText error>{String(errors.step1.saat)}</FormHelperText>
						)}
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
};

export default Step1SalonTarih;
